import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import { ClanRequest } from '../../types/clan';
import clanService from '../../services/clanService';
import FieldInfoButton from '../field/FieldInfoButton';
import ConfirmModal from '../common/ConfirmModal';
import { useConfirmModal } from '../../hooks/useConfirmModal';
import { useNotification } from '../../hooks/useNotification';
import NotificationContainer from '../common/NotificationContainer';
import { clanInvitationsManagementInfo } from './clanRequestsInfo';

const withAlpha = (hexColor: string, alpha: number) => {
  const hex = hexColor.replace('#', '');
  const normalized = hex.length === 3
    ? hex.split('').map(char => `${char}${char}`).join('')
    : hex;
  const bigint = parseInt(normalized, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const isModernTheme = (theme: any) => theme.name === 'modernWarfare';
const isShadowTheme = (theme: any) => theme.name === 'shadowGrid';

interface ClanInvitationsTabProps {
  clanId: string;
  canManage: boolean;
  isActive?: boolean;
}

const ClanInvitationsTab: React.FC<ClanInvitationsTabProps> = ({ clanId, canManage, isActive = true }) => {
  const { confirm, modalProps } = useConfirmModal();
  const { notifications, removeNotification, showError } = useNotification();
  const navigate = useNavigate();
  const theme = useTheme();
  const [invitations, setInvitations] = useState<ClanRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isActive && clanId) {
      loadInvitations();
    }
  }, [clanId, isActive]);

  const loadInvitations = async () => {
    if (!canManage) {
      setError('You don\'t have permission to view invitations');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await clanService.getClanInvitations(clanId);
      setInvitations(data);
    } catch (err: any) {
      console.error('Error loading invitations:', err);
      setError(err.message || 'Failed to load invitations');
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawInvitation = async (invitation: ClanRequest) => {
    if (processingIds.has(invitation.userId)) return;

    const confirmed = await confirm({
      title: 'Withdraw Invitation',
      message: `Are you sure you want to withdraw the invitation to ${invitation.userName}?`,
      confirmText: 'Withdraw',
      cancelText: 'Cancel',
      variant: 'warning',
    });
    if (!confirmed) return;

    try {
      setProcessingIds(prev => new Set(prev).add(invitation.userId));
      await clanService.withdrawInvitation(clanId, invitation.userId);

      // Reload invitations after withdrawal
      await loadInvitations();
    } catch (err: any) {
      console.error('Error withdrawing invitation:', err);
      showError('Error', err.message || 'Failed to withdraw invitation');
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(invitation.userId);
        return newSet;
      });
    }
  };


  const handleUserClick = (userId: string) => {
    navigate(`/profile/${userId}`);
  };

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>Loading invitations...</LoadingSpinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
        <RetryButton onClick={loadInvitations}>Retry</RetryButton>
      </Container>
    );
  }

  if (!canManage) {
    return (
      <Container>
        <ErrorMessage>You don't have permission to view invitations</ErrorMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <TitleRow>
          <Title>Sent Invitations ({invitations.length})</Title>
          <FieldInfoButton title="Managing Invitations" sections={clanInvitationsManagementInfo} />
        </TitleRow>
        <HeaderActions>
          <RefreshButton onClick={loadInvitations} disabled={loading}>
            🔄 Refresh
          </RefreshButton>
        </HeaderActions>
      </Header>

      {invitations.length === 0 ? (
        <EmptyState>
          <EmptyIcon>✉️</EmptyIcon>
          <EmptyTitle>No Invitations Sent</EmptyTitle>
          <EmptyDescription>
            You haven't sent any invitations yet. Use the "Invite Member" button in the sidebar to get started.
          </EmptyDescription>
        </EmptyState>
      ) : (
        <InvitationsList>
          {invitations.map((invitation) => (
            <InvitationCard key={invitation.id}>
              <InvitationInfo onClick={() => handleUserClick(invitation.userId)}>
                <UserName>{invitation.userName}</UserName>
                <InvitationDetails>
                  <InvitationDate>
                    Invited {clanService.formatRequestDate(invitation.createdAt)}
                  </InvitationDate>
                  <InvitedBy>by {invitation.createdByName}</InvitedBy>
                </InvitationDetails>
                <ClanType>{invitation.clanType}</ClanType>
              </InvitationInfo>
              
              <InvitationActions>
                <StatusBadge status={invitation.status}>
                  {invitation.status}
                </StatusBadge>
                <WithdrawButton
                  onClick={() => handleWithdrawInvitation(invitation)}
                  disabled={processingIds.has(invitation.userId)}
                >
                  {processingIds.has(invitation.userId) ? '...' : '🗑️ Withdraw'}
                </WithdrawButton>
              </InvitationActions>
            </InvitationCard>
          ))}
        </InvitationsList>
      )}

      <NotificationContainer notifications={notifications} onRemove={removeNotification} />
      <ConfirmModal {...modalProps} />
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 20px;
  background: transparent;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Title = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.5rem;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "'Courier New', monospace"};
`;

const RefreshButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => isShadowTheme(theme) ? '#000000' : '#00121f'};
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  transition: all 0.3s ease;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Chakra Petch', sans-serif" : "'Courier New', monospace"};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.primaryDark};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;


const LoadingSpinner = styled.div`
  text-align: center;
  padding: 40px;
  color: ${({ theme }) => withAlpha(theme.colors.text, 0.7)};
`;

const ErrorMessage = styled.div`
  background: ${({ theme }) => withAlpha(theme.colors.error, 0.1)};
  color: ${({ theme }) => theme.colors.error};
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.error, 0.3)};
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 15px;
`;

const RetryButton = styled.button`
  background: ${({ theme }) => theme.colors.error};
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => withAlpha(theme.colors.error, 0.8)};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${({ theme }) => withAlpha(theme.colors.text, 0.6)};
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h4`
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  color: ${({ theme }) => withAlpha(theme.colors.text, 0.8)};
`;

const EmptyDescription = styled.p`
  margin: 0;
  font-size: 1rem;
  color: ${({ theme }) => withAlpha(theme.colors.text, 0.6)};
  opacity: 0.8;
`;

const InvitationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InvitationCard = styled.div`
  background: ${({ theme }) => withAlpha(theme.colors.surface, 0.8)};
  backdrop-filter: blur(10px);
  border: 1px solid ${({ theme }) => withAlpha(theme.colors.primary, 0.2)};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const InvitationInfo = styled.div`
  flex: 1;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 4px;
  border-radius: 6px;
  
  &:hover {
    background: ${({ theme }) => withAlpha(theme.colors.primary, 0.1)};
    transform: translateY(-1px);
  }
`;

const UserName = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${({ theme }) => withAlpha(theme.colors.text, 0.9)};
  margin-bottom: 4px;
  transition: color 0.2s ease;
  font-family: ${({ theme }) => isModernTheme(theme) ? "'Rajdhani', sans-serif" : "'Courier New', monospace"};
  
  ${InvitationInfo}:hover & {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const InvitationDetails = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 4px;
`;

const InvitationDate = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => withAlpha(theme.colors.text, 0.6)};
`;

const InvitedBy = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => withAlpha(theme.colors.text, 0.6)};
  font-style: italic;
`;

const ClanType = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.secondary};
  text-transform: capitalize;
  font-weight: 500;
`;

const InvitationActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  background: ${props => 
    props.status === 'pending' ? '#fff3cd' :
    props.status === 'accepted' ? '#d1edff' :
    props.status === 'declined' ? '#f8d7da' : '#e9ecef'};
  color: ${props => 
    props.status === 'pending' ? '#856404' :
    props.status === 'accepted' ? '#0c5460' :
    props.status === 'declined' ? '#721c24' : '#495057'};
`;

const WithdrawButton = styled.button`
  background: ${({ theme }) => theme.colors.error};
  color: white;
  border: none;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => withAlpha(theme.colors.error, 0.8)};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;


export default ClanInvitationsTab;